import React, { useContext, useState, FormEvent } from 'react';

import { Button, PopUp, Dialog, DialogHeader, DialogContent, Input } from '@ergeon/core-components';
import { ArrowRightIcon } from '@ergeon/icons/hero/mini';

import { type ModalHandler } from '../index';
import { TwilioContext } from '../../context-providers/TwilioContext';
import useCallControls from '../../hooks/useCallControls';
import { useModalClose } from '../../hooks/useModalClose';

type Props = {modal: ModalHandler};

const DTMFModal: React.FC<Props> = ({ modal }) => {
  const [digits, setDigits] = useState<string>('');
  const { currentCall } = useContext(TwilioContext);
  const { sendDigits } = useCallControls(currentCall);
  const closeModal = useModalClose(modal);

  const handleFormSubmit = async(e: FormEvent) => {
    e.preventDefault();
    if (digits && currentCall) {
      try {
        await sendDigits(digits);
        setDigits('');
        closeModal();
      } catch (error) {
        console.error('Failed to send DTMF digits:', error);
      }
    }
  };

  const handleDigitsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits, #, *, and + characters
    const value = e.target.value.replace(/[^0-9#*+]+/g, '');
    setDigits(value);
  };

  return (
    <PopUp isVisible={modal.visible} maxWidth="medium" onChangeVisible={closeModal}>
      <Dialog isVisible={modal.visible}>
        <DialogHeader onDismiss={closeModal} title="DTMF Dialer" />
        <DialogContent>
          <form className="flex flex-col gap-4" data-testid="dtmf-modal-form" onSubmit={handleFormSubmit}>
            <div className="flex flex-col gap-2">
              <Input
                aria-label="Enter DTMF digits to send"
                label="DTMF Digits"
                onChange={handleDigitsChange}
                placeholder="Enter digits (0-9, #, *, +)"
                type="text"
                value={digits} />
              <div className="text-gray-600 dark:text-gray-400 text-sm">Send touch-tone signals to the active call</div>
            </div>

            <div className="flex gap-2">
              <Button
                flavor="regular" isFullWidth onClick={closeModal} taste="line"
                type="button">
                Cancel
              </Button>
              <Button
                aria-label="Send DTMF tones to current call"
                disabled={!digits || !currentCall}
                flavor="success"
                isFullWidth
                title="Send digits to the current call"
                type="submit">
                <ArrowRightIcon className="size-5 shrink-0" />
                Send Digits
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </PopUp>
  );
};

export default DTMFModal;
